import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { blogData } from '@/mocks/homeData';

function BlogCard({ blog, index }: { blog: typeof blogData[0]; index: number }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Link
      to={`/blog/${blog.slug}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="reveal opacity-0 translate-y-6 transition-all duration-700 group bg-firmai-card border border-firmai-border rounded-xl overflow-hidden hover:border-firmai-green/30 transition-all duration-500"
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={blog.image}
          alt={blog.title}
          className={`w-full h-full object-cover transition-transform duration-700 ${
            isHovered ? 'scale-110' : 'scale-100'
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-firmai-card to-transparent" />
        <span className="absolute top-3 left-3 px-3 py-1 bg-firmai-green/20 text-firmai-green text-xs font-medium rounded-full border border-firmai-green/30 backdrop-blur-sm">
          {blog.category}
        </span>

        {/* Read overlay on hover */}
        <div className={`absolute inset-0 bg-firmai-dark/60 flex items-center justify-center transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <span className="px-4 py-2 bg-firmai-green text-firmai-dark text-sm font-medium rounded-full">
            点击阅读
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 md:p-6">
        <div className="text-firmai-muted text-xs mb-2">{blog.date}</div>
        <h3 className="text-white font-semibold text-sm md:text-base leading-snug group-hover:text-firmai-green transition-colors duration-300 line-clamp-2">
          {blog.title}
        </h3>
        <p className="text-firmai-text text-xs md:text-sm mt-2 line-clamp-2 leading-relaxed">
          {blog.excerpt}
        </p>
        <div className="flex items-center gap-1 text-firmai-green text-sm font-medium mt-4 group-hover:gap-2 transition-all duration-200">
          阅读全文
          <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-200">
            <i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="blog"
      ref={sectionRef}
      className="relative py-20 md:py-28 bg-firmai-dark"
    >
      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 flex items-center justify-between mb-12">
          <div>
            <span className="text-firmai-green text-sm font-semibold tracking-wider uppercase">
              行业洞察
            </span>
            <h2 className="font-display font-bold text-2xl md:text-4xl text-white mt-3">
              最新动态与专业见解
            </h2>
          </div>
          <Link
            to="/blog"
            className="hidden md:inline-flex items-center gap-1 text-firmai-green text-sm font-medium hover:gap-2 transition-all duration-200 group"
          >
            查看全部
            <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-200">
              <i className="ri-arrow-right-line" />
            </span>
          </Link>
        </div>

        {/* Blog Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {blogData.map((blog, index) => (
            <BlogCard key={blog.title} blog={blog} index={index} />
          ))}
        </div>

        {/* Mobile View All */}
        <div className="md:hidden text-center mt-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 text-firmai-green text-sm font-medium"
          >
            查看全部文章
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </span>
          </Link>
        </div>
      </div>

      <style>{`
        .reveal.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </section>
  );
}